import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/common/table/table";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RefreshCcwIcon } from "lucide-react";
import dayjs from "dayjs";

import { Ellipse } from "@/components/common/svgs";
import { clearLoadSearchState, fetchLoads, refreshLoads } from "@/state/loadSearchSlice";
import { Skeleton } from "@/components/common/other/Skeleton";
import { ErrorComponent } from "@/components/common/errors";
import { Pagination } from "@/components/common/pagination/Pagination";
import { LoadboardTableCell, LoadboardTableRow, TableWidths, MyOffers, SortableTableHead } from "@/components/dataPages/loadboard";
import { setSearchType } from "@/state/searchInputSlice";
import { TranslatedText } from "@/components/common/text";
import { useQuery } from "@/utils/query";
import { useNavigate, useSearchParams } from "react-router";
import { protectedRoutes } from "@/routing";
import { MultiSelectDropdown } from "@/components/common/inputs";

export const Loadboard = () => {
    const dispatch = useDispatch();
    const { loads, loading, error } = useSelector((state) => state.loadSearch);
    const {
        trailerType,
        pickupZip,
        pickupPlaceName,
        pickupStateCode,
        deliveryZip,
        deliveryPlaceName,
        deliveryStateCode,
        pickupDate,
        pickupDateEnd,
        deliveryDate,
        deliveryDateEnd,
        pickupRadius,
        deliveryRadius,
        page,
        sortBy,
        sortDirection,
        pickupMarket,
    } = useQuery();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const getLoadsIntervalRef = useRef(null);

    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(parseInt(page) || 1);

    const [selectedStartMarkets, setSelectedStartMarkets] = useState(
        pickupMarket
            ? {
                  [pickupMarket]: true,
              }
            : {},
    );
    const [selectedEndMarkets, setSelectedEndMarkets] = useState({});

    // Default sort is by age (descending - newest first)
    const currentSort = {
        key: sortBy || "age",
        direction: sortDirection || "desc",
    };

    const handleSort = (key, direction) => {
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set("sortBy", key);
        updatedParams.set("sortDirection", direction);
        updatedParams.set("page", "1"); // Reset to first page when sorting
        navigate(`${protectedRoutes[6].path}?${updatedParams.toString()}`);
    };

    useEffect(() => {
        dispatch(setSearchType("loadboard"));
    }, []);

    const clearLoadsInterval = () => {
        if (getLoadsIntervalRef.current) {
            clearInterval(getLoadsIntervalRef.current);
        }
    };

    const getLoads = () => {
        const pickup = { zip: pickupZip, countryCode: "US", placeName: pickupPlaceName, stateCode: pickupStateCode };
        const delivery = deliveryZip
            ? { zip: deliveryZip, countryCode: "US", placeName: deliveryPlaceName, stateCode: deliveryStateCode }
            : undefined;

        dispatch(
            fetchLoads({
                trailerType,
                pickup,
                delivery,
                pickupDate,
                pickupDateEnd,
                deliveryDate,
                deliveryDateEnd,
                pickupRadius: pickupRadius === "market-area" ? null : pickupRadius,
                deliveryRadius: deliveryRadius === "market-area" ? null : deliveryRadius,
            }),
        );

        clearLoadsInterval();
        getLoadsIntervalRef.current = setInterval(() => {
            dispatch(
                refreshLoads({
                    trailerType,
                    pickup,
                    delivery,
                    pickupDate,
                    pickupDateEnd,
                    deliveryDate,
                    deliveryDateEnd,
                    pickupRadius: pickupRadius === "market-area" ? null : pickupRadius,
                    deliveryRadius: deliveryRadius === "market-area" ? null : deliveryRadius,
                }),
            );
        }, 60000);
    };

    useEffect(() => {
        if (trailerType && pickupZip) {
            getLoads();
        }
    }, [trailerType, pickupZip, deliveryZip, pickupDate, pickupDateEnd, deliveryDate, deliveryDateEnd, pickupRadius, deliveryRadius]);

    useEffect(() => {
        setCurrentPage(parseInt(page) || 1);
    }, [page]);

    // Reset to first page when market filters change
    useEffect(() => {
        if (currentPage !== 1) {
            const updatedParams = new URLSearchParams(searchParams);
            updatedParams.set("page", "1");
            navigate(`${protectedRoutes[6].path}?${updatedParams.toString()}`, { replace: true });
        }
    }, [selectedStartMarkets, selectedEndMarkets]);

    // Filter and sort loads based on current sort settings and market filters
    const sortedLoads = useMemo(() => {
        if (!loads.data) return [];

        // Filter loads based on selected markets
        let filteredLoads = [...loads.data];

        // Filter by start markets (pickup)
        const selectedStartMarketKeys = Object.keys(selectedStartMarkets).filter((key) => selectedStartMarkets[key]);
        if (selectedStartMarketKeys.length > 0) {
            filteredLoads = filteredLoads.filter((load) => {
                const firstStop = load.itineraryStops[0];
                return firstStop?.marketName && selectedStartMarketKeys.includes(firstStop.marketName);
            });
        }

        // Filter by end markets (delivery)
        const selectedEndMarketKeys = Object.keys(selectedEndMarkets).filter((key) => selectedEndMarkets[key]);
        if (selectedEndMarketKeys.length > 0) {
            filteredLoads = filteredLoads.filter((load) => {
                const lastStop = load.itineraryStops[load.itineraryStops.length - 1];
                return lastStop?.marketName && selectedEndMarketKeys.includes(lastStop.marketName);
            });
        }

        // Sort the filtered loads
        const sorted = filteredLoads.sort((a, b) => {
            let aValue, bValue;

            switch (currentSort.key) {
                case "age":
                    aValue = a.updatedAt || a.createdAt ? dayjs(a.updatedAt || a.createdAt).unix() : 0;
                    bValue = b.updatedAt || b.createdAt ? dayjs(b.updatedAt || b.createdAt).unix() : 0;
                    break;
                case "pickup":
                    aValue = a.itineraryStops[0]?.stopCity || "";
                    bValue = b.itineraryStops[0]?.stopCity || "";
                    break;
                case "delivery":
                    aValue = a.itineraryStops[a.itineraryStops.length - 1]?.stopCity || "";
                    bValue = b.itineraryStops[b.itineraryStops.length - 1]?.stopCity || "";
                    break;
                case "pickupDate":
                    // Combine date with time from itinerary stop
                    if (a.pickupDate) {
                        const pickupStop = a.itineraryStops[0];
                        const pickupTime = pickupStop?.earlyTime || pickupStop?.lateTime || "00:00";
                        aValue = dayjs(`${a.pickupDate} ${pickupTime}`).unix();
                    } else {
                        aValue = 0;
                    }
                    if (b.pickupDate) {
                        const pickupStop = b.itineraryStops[0];
                        const pickupTime = pickupStop?.earlyTime || pickupStop?.lateTime || "00:00";
                        bValue = dayjs(`${b.pickupDate} ${pickupTime}`).unix();
                    } else {
                        bValue = 0;
                    }
                    break;
                case "deliveryDate":
                    // Combine date with time from itinerary stop
                    if (a.deliveryDate) {
                        const deliveryStop = a.itineraryStops[a.itineraryStops.length - 1];
                        const deliveryTime = deliveryStop?.earlyTime || deliveryStop?.lateTime || "00:00";
                        aValue = dayjs(`${a.deliveryDate} ${deliveryTime}`).unix();
                    } else {
                        aValue = 0;
                    }
                    if (b.deliveryDate) {
                        const deliveryStop = b.itineraryStops[b.itineraryStops.length - 1];
                        const deliveryTime = deliveryStop?.earlyTime || deliveryStop?.lateTime || "00:00";
                        bValue = dayjs(`${b.deliveryDate} ${deliveryTime}`).unix();
                    } else {
                        bValue = 0;
                    }
                    break;
                case "distance":
                    aValue = a.loadDistance || 0;
                    bValue = b.loadDistance || 0;
                    break;
                case "broker":
                    aValue = a.brokerInfo?.name || "";
                    bValue = b.brokerInfo?.name || "";
                    break;
                case "offer":
                    aValue = a.offerRate || 0;
                    bValue = b.offerRate || 0;
                    break;
                default:
                    return 0;
            }

            // Handle string comparison
            if (typeof aValue === "string" && typeof bValue === "string") {
                return currentSort.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            // Handle numeric comparison
            return currentSort.direction === "asc" ? aValue - bValue : bValue - aValue;
        });

        return sorted;
    }, [loads.data, currentSort.key, currentSort.direction, selectedStartMarkets, selectedEndMarkets]);

    const totalItems = sortedLoads.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = sortedLoads.slice(startIndex, endIndex);

    const loadMarkets = useMemo(() => {
        if (!loads.data || loads.data.length === 0) {
            return {};
        }

        const startMarkets = {};
        const endMarkets = {};

        loads.data.forEach((load) => {
            const firstStop = load.itineraryStops[0];
            const lastStop = load.itineraryStops[load.itineraryStops.length - 1];
            if (firstStop?.marketName) {
                const marketName = firstStop?.marketName;
                startMarkets[marketName] = (startMarkets[marketName] || 0) + 1;
            }
            if (lastStop?.marketName) {
                const marketName = lastStop?.marketName;
                endMarkets[marketName] = (endMarkets[marketName] || 0) + 1;
            }
        });

        return {
            startMarkets,
            endMarkets,
        };
    }, [loads]);

    useEffect(() => {
        return () => {
            clearLoadsInterval();
            dispatch(clearLoadSearchState());
        };
    }, []);

    useEffect(() => {
        if (error) {
            clearLoadsInterval();
        }
    }, [error]);

    return (
        <div className="text-cm-text flex flex-col items-center justify-start md:justify-center w-full grow min-h-full max-w-[1440px]">
            {loading ? (
                <div className="min-h-[354px]">
                    <Skeleton />
                </div>
            ) : error ? (
                <ErrorComponent className="min-h-[354px]" text={error} />
            ) : loads.data ? (
                <div className="w-full">
                    <div className="text-cm-text-primary text-lg/[28px] font-medium text-left mb-4 w-full flex flex-row justify-between">
                        <div className="flex flex-row items-center gap-2 pl-4">
                            <Ellipse isAvailable={sortedLoads.filter((l) => !l.isUnavailable).length} />
                            <span>
                                {sortedLoads.length} <TranslatedText>loads available</TranslatedText>
                            </span>
                            <button
                                className="ml-4 text-sm bg-cm-p-surface-container rounded-[38px] py-[6px] px-4 inline-flex items-center justify-center gap-2"
                                onClick={getLoads}
                            >
                                <RefreshCcwIcon className="w-4 text-cm-accent" />
                                <TranslatedText>Refresh</TranslatedText>
                            </button>
                        </div>
                        <div className="flex flex-row items-center gap-4">
                            <MultiSelectDropdown
                                label="Included pickup markets"
                                items={loadMarkets.startMarkets}
                                value={selectedStartMarkets}
                                setValue={setSelectedStartMarkets}
                            />
                            <MultiSelectDropdown
                                label="Included delivery markets"
                                items={loadMarkets.endMarkets}
                                value={selectedEndMarkets}
                                setValue={setSelectedEndMarkets}
                            />
                        </div>
                    </div>

                    <div className="border border-cm-p-outline rounded-lg overflow-hidden w-full">
                        <Table className="w-full h-12">
                            <TableHeader className="bg-cm-p-surface-container h-12">
                                <TableRow className="h-12 border-0">
                                    <TableHead className={`${TableWidths[0]}`} />
                                    <SortableTableHead
                                        className={`${TableWidths[1]}`}
                                        sortKey="age"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Age
                                    </SortableTableHead>
                                    <TableHead className={`${TableWidths[2]}`}>LoadID</TableHead>

                                    <SortableTableHead
                                        className={`${TableWidths[3]}`}
                                        sortKey="pickup"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Pickup
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[4]}`}
                                        sortKey="delivery"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Delivery
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[5]}`}
                                        sortKey="pickupDate"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Pickup date
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[6]}`}
                                        sortKey="deliveryDate"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Delivery date
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[7]}`}
                                        sortKey="distance"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Distance
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[8]}`}
                                        sortKey="broker"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Broker
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[9]}`}
                                        sortKey="offer"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Offer
                                    </SortableTableHead>
                                    <TableHead className={`${TableWidths[10]}`} />
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {currentItems?.length ? (
                                    currentItems.map((load, index) => {
                                        return <LoadboardTableRow key={index} load={load} index={index} />;
                                    })
                                ) : (
                                    <TableRow className="h-12 border-0">
                                        <LoadboardTableCell colSpan={11} innerClassName="h-12">
                                            <div className="w-full h-full flex items-center justify-center">
                                                <TranslatedText text="Couldn't find anything for your current search" />
                                            </div>
                                        </LoadboardTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-[51px]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                itemsPerPage={itemsPerPage}
                                totalItems={sortedLoads.length}
                                onPageChange={(newPage) => {
                                    const updatedParams = new URLSearchParams(searchParams);
                                    updatedParams.set("page", newPage);
                                    navigate(`${protectedRoutes[6].path}?${updatedParams.toString()}`);
                                }}
                                itemsLabel="loads"
                            />
                        </div>
                    )}
                </div>
            ) : null}
            <MyOffers />
        </div>
    );
};
